/* Angel Kiss PDP — interaction layer.
   Vanilla custom elements: each owns its listeners and cleans up in
   disconnectedCallback, so Theme Editor section add/remove/reorder/reload
   re-initialises cleanly without duplicate handlers. Add-to-cart submission is
   delegated to the theme's own <product-form> (product-form.js), which renders
   the theme cart drawer. */
(function () {
  'use strict';
  if (window.AKPDP) return;

  var reduceMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };
  var MOBILE = '(max-width: 899px)';
  var isMobile = function () { return window.matchMedia(MOBILE).matches; };

  /* ---------- Money: format derived from a server-rendered sample ---------- */
  function MoneyFormatter(sample) {
    // sample = money(123456789) in the active currency, e.g. "$1,234,567.89" or "1.234.567,89 €"
    var m = /(\d[\d.,'\s  ]*\d)/.exec(sample || '');
    this.ok = !!m;
    if (!m) return;
    var num = m[1];
    this.prefix = sample.slice(0, m.index);
    this.suffix = sample.slice(m.index + num.length);
    var decMatch = /[.,](\d{2})$/.exec(num);
    this.decimals = decMatch ? 2 : 0;
    this.dec = decMatch ? num.charAt(num.length - 3) : '';
    var intPart = decMatch ? num.slice(0, -3) : num;
    var sepMatch = /\D/.exec(intPart);
    this.sep = sepMatch ? sepMatch[0] : '';
  }
  MoneyFormatter.prototype.format = function (cents) {
    if (!this.ok) return (cents / 100).toFixed(2);
    var value = this.decimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
    var parts = value.split('.');
    var intStr = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.sep);
    return this.prefix + intStr + (this.decimals ? this.dec + parts[1] : '') + this.suffix;
  };

  /* ---------- Reveal on scroll ---------- */
  var revealIO = null;
  function initReveal(root) {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return; // keep editor preview static
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('ak-reveal-in'); revealIO.unobserve(en.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    (root || document).querySelectorAll('[data-ak-reveal]').forEach(function (el) {
      if (el.dataset.akRevealBound) return;
      el.dataset.akRevealBound = '1';
      if (el.getBoundingClientRect().top < window.innerHeight) return; // above the fold: never hide
      el.classList.add('ak-reveal-init');
      revealIO.observe(el);
    });
  }

  function scrollToEl(el, offset) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.scrollY - (offset || 100);
    window.scrollTo({ top: top, behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  /* ======================================================================
     <ak-product> — gallery, variants, quantity, ATC label, sticky bar
     ====================================================================== */
  class AkProduct extends HTMLElement {
    connectedCallback() {
      var json = this.querySelector('script[data-ak-product-json]');
      if (!json) return;
      try { this.data = JSON.parse(json.textContent); } catch (e) { console.error('[ak-pdp] product JSON', e); return; }
      this.money = new MoneyFormatter(this.data.money_sample);
      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.qtyInput = this.querySelector('[data-ak-qty-input]');
      this.track = this.querySelector('[data-ak-track]');
      this.slides = this.track ? Array.prototype.slice.call(this.track.querySelectorAll('[data-ak-slide]')) : [];
      this.index = 0;
      this.ac = new AbortController();
      var sig = { signal: this.ac.signal };

      // Options
      this.addEventListener('change', this.onChange.bind(this), sig);
      this.addEventListener('click', this.onClick.bind(this), sig);

      // Gallery
      if (this.track) {
        this.track.addEventListener('scroll', this.onTrackScroll.bind(this), { signal: this.ac.signal, passive: true });
        this.track.addEventListener('keydown', this.onTrackKey.bind(this), sig);
      }

      // Cross-section: shade cards ask us to select an option value
      document.addEventListener('ak:select-option', this.onExternalSelect.bind(this), sig);
      window.addEventListener('resize', this.onResize.bind(this), { signal: this.ac.signal, passive: true });

      // Sticky add-to-cart
      this.sticky = this.querySelector('[data-ak-sticky]');
      var buyRow = this.querySelector('[data-ak-buy]');
      if (this.sticky && buyRow) {
        document.body.appendChild(this.sticky); // escape transformed ancestors for position:fixed
        document.body.classList.add('ak-has-sticky');
        this.buyRow = buyRow;
        var ticking = false, self = this;
        var onScroll = function () {
          if (ticking) return; ticking = true;
          window.requestAnimationFrame(function () { ticking = false; self.onBuyVisibility(); });
        };
        window.addEventListener('scroll', onScroll, { signal: this.ac.signal, passive: true });
        window.addEventListener('resize', onScroll, { signal: this.ac.signal, passive: true });
        this.onBuyVisibility();
      }

      // Keep a tall panel usable while sticky
      this.panel = this.querySelector('[data-ak-panel]');
      if (this.panel && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.fitPanel.bind(this));
        this.ro.observe(this.panel);
      }

      this.currentVariant = this.findVariant(this.getSelectedOptions()) || null;
      this.updateOptionAvailability();
      this.updateQtyButtons();
      this.updateCounter();
      initReveal(document);
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.ro) this.ro.disconnect();
      if (this.sticky && this.sticky.parentNode === document.body) this.sticky.remove();
      document.body.classList.remove('ak-has-sticky');
    }

    /* ----- options / variants ----- */
    getSelectedOptions() {
      var names = this.data.options;
      return names.map(function (name, i) {
        var checked = this.querySelector('input[data-ak-option-index="' + i + '"]:checked');
        return checked ? checked.value : null;
      }, this);
    }
    findVariant(opts) {
      return this.data.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }
    onChange(e) {
      var t = e.target;
      if (t.matches('input[data-ak-option-index]')) {
        var legendVal = this.querySelector('[data-ak-legend-value="' + t.dataset.akOptionIndex + '"]');
        if (legendVal) legendVal.textContent = t.value;
        this.selectVariant(this.findVariant(this.getSelectedOptions()), true);
      } else if (t === this.qtyInput) {
        this.clampQty();
        this.updateAtc();
      }
    }
    onExternalSelect(e) {
      var d = e.detail || {};
      var input = this.querySelector('input[data-ak-option-index="' + d.index + '"][value="' + CSS.escape(d.value) + '"]');
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (d.scroll) scrollToEl(this.querySelector('[data-ak-product-top]') || this, 100);
    }
    selectVariant(variant, fromUser) {
      this.currentVariant = variant || null;
      this.updateOptionAvailability();
      if (this.idInput) this.idInput.value = variant ? variant.id : '';
      this.updatePrice();
      this.updateAvailability();
      this.updateQtyRules();
      this.updateAtc();
      this.updateSticky();
      if (variant && fromUser) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (err) {}
        if (variant.media_id) this.goToMedia(variant.media_id, false);
      }
      var detail = { sectionId: this.dataset.sectionId, variant: variant, options: this.getSelectedOptions() };
      document.dispatchEvent(new CustomEvent('ak:variant-change', { detail: detail }));
    }
    updateOptionAvailability() {
      var sel = this.getSelectedOptions();
      var variants = this.data.variants;
      this.querySelectorAll('input[data-ak-option-index]').forEach(function (input) {
        var idx = +input.dataset.akOptionIndex;
        var val = input.value;
        var match = variants.filter(function (v) {
          return v.options[idx] === val && v.options.every(function (o, j) { return j === idx || o === sel[j]; });
        });
        var available = match.some(function (v) { return v.available; });
        var label = input.nextElementSibling;
        if (available) {
          input.removeAttribute('data-unavailable');
          if (label && label.dataset.akLabel) label.setAttribute('aria-label', label.dataset.akLabel);
        } else {
          input.setAttribute('data-unavailable', '');
          if (label && label.dataset.akLabelSoldout) label.setAttribute('aria-label', label.dataset.akLabelSoldout);
        }
      });
    }
    updatePrice() {
      var v = this.currentVariant;
      var now = this.querySelectorAll('[data-ak-price]');
      var was = this.querySelectorAll('[data-ak-compare]');
      if (!v) return;
      var self = this;
      now.forEach(function (el) { el.textContent = self.money.format(v.price); });
      was.forEach(function (el) { el.textContent = v.compare_at_price > v.price ? self.money.format(v.compare_at_price) : ''; });
      var unit = this.querySelector('[data-ak-unit]');
      if (unit) { unit.textContent = v.unit_price || ''; unit.hidden = !v.unit_price; }
    }
    updateAvailability() {
      var el = this.querySelector('[data-ak-avail]');
      var v = this.currentVariant;
      var state = !v ? 'unavailable' : !v.available ? 'soldout' : v.preorder ? 'preorder' : 'instock';
      if (el) {
        el.dataset.state = state;
        var text = el.querySelector('[data-ak-avail-text]');
        if (text) text.textContent = el.dataset['text' + state.charAt(0).toUpperCase() + state.slice(1)] || '';
      }
      this.querySelectorAll('[data-ak-preorder-tag]').forEach(function (t) { t.hidden = state !== 'preorder'; });
    }
    updateQtyRules() {
      if (!this.qtyInput) return;
      var v = this.currentVariant;
      var min = v && v.qty_min ? v.qty_min : 1;
      var step = v && v.qty_step ? v.qty_step : 1;
      var max = v && v.qty_max ? v.qty_max : 99;
      this.qtyInput.min = min; this.qtyInput.step = step; this.qtyInput.max = max;
      this.clampQty();
    }
    clampQty() {
      if (!this.qtyInput) return;
      var min = +this.qtyInput.min || 1, max = +this.qtyInput.max || 99, step = +this.qtyInput.step || 1;
      var q = parseInt(this.qtyInput.value, 10);
      if (isNaN(q)) q = min;
      q = Math.max(min, Math.min(max, q));
      if ((q - min) % step) q = q - ((q - min) % step);
      this.qtyInput.value = q;
      this.updateQtyButtons();
    }
    updateQtyButtons() {
      if (!this.qtyInput) return;
      var q = +this.qtyInput.value;
      var dec = this.querySelector('[data-ak-qty="-1"]');
      var inc = this.querySelector('[data-ak-qty="1"]');
      if (dec) dec.disabled = q <= (+this.qtyInput.min || 1);
      if (inc) inc.disabled = q >= (+this.qtyInput.max || 99);
    }
    updateAtc() {
      var v = this.currentVariant;
      var qty = this.qtyInput ? (+this.qtyInput.value || 1) : 1;
      var buttons = [];
      if (this.form) buttons = buttons.concat(Array.prototype.slice.call(this.form.querySelectorAll('[data-ak-atc]')));
      if (this.sticky) buttons = buttons.concat(Array.prototype.slice.call(this.sticky.querySelectorAll('[data-ak-atc]')));
      var ds = this.dataset;
      var self = this;
      buttons.forEach(function (btn) {
        var label = btn.querySelector('[data-ak-atc-label]');
        var text;
        if (!v) { text = ds.textUnavailable; btn.disabled = true; }
        else if (!v.available) { text = ds.textSoldout; btn.disabled = true; }
        else {
          btn.disabled = false;
          text = v.preorder && ds.textPreorder ? ds.textPreorder : ds.textAdd;
          if (btn.dataset.akShowPrice === 'true') text += ds.priceSeparator + self.money.format(v.price * qty);
        }
        btn.removeAttribute('aria-disabled');
        if (label) label.textContent = text;
      });
      // Dawn's product-form reads sold-out state from aria-disabled; clear any stale error
      var err = this.form && this.form.closest('product-form');
      if (err && err.handleErrorMessage) err.handleErrorMessage();
    }
    updateSticky() {
      if (!this.sticky) return;
      var v = this.currentVariant;
      var name = this.sticky.querySelector('[data-ak-sticky-variant]');
      if (name) name.textContent = v ? v.title : '';
      var price = this.sticky.querySelector('[data-ak-sticky-price]');
      if (price && v) price.textContent = this.money.format(v.price);
      var img = this.sticky.querySelector('[data-ak-sticky-img]');
      if (img && v && v.image) { img.src = v.image; img.hidden = false; }
    }
    onBuyVisibility() {
      if (!this.sticky || !this.buyRow) return;
      var show = this.buyRow.getBoundingClientRect().bottom < 0;
      if (this._stickyShown === show) return;
      this._stickyShown = show;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.sticky.querySelectorAll('button').forEach(function (b) { b.tabIndex = show ? 0 : -1; });
    }
    fitPanel() {
      if (!this.panel || isMobile()) { if (this.panel) this.panel.style.top = ''; return; }
      var base = parseInt(getComputedStyle(this).getPropertyValue('--ak-sticky-top'), 10) || 32;
      var h = this.panel.offsetHeight;
      var room = window.innerHeight - h - 24;
      this.panel.style.top = (room < base ? room : base) + 'px';
    }
    onResize() {
      this.fitPanel();
      if (this.track && this.slides.length) this.track.scrollLeft = this.index * this.track.clientWidth;
    }

    /* ----- clicks: gallery controls, qty, accordions ----- */
    onClick(e) {
      var t = e.target.closest('button, a');
      if (!t || !this.contains(t)) return;
      if (t.hasAttribute('data-ak-prev')) { this.goTo(this.index - 1, true); }
      else if (t.hasAttribute('data-ak-next')) { this.goTo(this.index + 1, true); }
      else if (t.hasAttribute('data-ak-thumb')) { this.goTo(+t.dataset.akThumb, true); }
      else if (t.hasAttribute('data-ak-qty')) {
        e.preventDefault();
        var step = +this.qtyInput.step || 1;
        this.qtyInput.value = (+this.qtyInput.value || 1) + (+t.dataset.akQty) * step;
        this.clampQty();
        this.updateAtc();
      } else if (t.hasAttribute('data-ak-jump')) {
        var target = document.querySelector(t.getAttribute('href'));
        if (target) { e.preventDefault(); scrollToEl(target, 70); }
      }
    }

    /* ----- gallery ----- */
    goTo(i, smooth) {
      if (!this.track || !this.slides.length) return;
      var k = Math.max(0, Math.min(this.slides.length - 1, i));
      this.index = k;
      this.track.scrollTo({ left: k * this.track.clientWidth, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      this.updateCounter();
    }
    goToMedia(mediaId, smooth) {
      var idx = this.slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
      if (idx > -1) this.goTo(idx, smooth);
    }
    onTrackScroll() {
      var t = this.track;
      var i = Math.round(t.scrollLeft / Math.max(1, t.clientWidth));
      if (i !== this.index) {
        this.index = i;
        this.updateCounter();
        this.pauseMedia();
      }
    }
    onTrackKey(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1, true); }
      else if (e.key === 'Home') { e.preventDefault(); this.goTo(0, true); }
      else if (e.key === 'End') { e.preventDefault(); this.goTo(this.slides.length - 1, true); }
    }
    pauseMedia() {
      this.slides.forEach(function (s, i) {
        s.querySelectorAll('video').forEach(function (v) { if (!v.paused) v.pause(); });
      });
    }
    updateCounter() {
      var n = this.slides.length;
      var c = this.querySelector('[data-ak-counter]');
      if (c) c.textContent = (this.index + 1) + ' / ' + n;
      var prev = this.querySelector('[data-ak-prev]'), next = this.querySelector('[data-ak-next]');
      if (prev) prev.disabled = this.index <= 0;
      if (next) next.disabled = this.index >= n - 1;
      var idx = this.index;
      this.querySelectorAll('[data-ak-thumb]').forEach(function (b) {
        var on = +b.dataset.akThumb === idx;
        b.setAttribute('aria-current', on ? 'true' : 'false');
        if (on && b.parentElement && b.parentElement.scrollHeight > b.parentElement.clientHeight) {
          var p = b.parentElement; var top = b.offsetTop - p.offsetTop;
          if (top < p.scrollTop || top + b.offsetHeight > p.scrollTop + p.clientHeight) p.scrollTop = top - 8;
        }
      });
      this.querySelectorAll('[data-ak-dot]').forEach(function (d) { d.classList.toggle('is-active', +d.dataset.akDot === idx); });
      this.slides.forEach(function (s, i) { s.setAttribute('aria-hidden', i === idx ? 'false' : 'true'); if ('inert' in s) s.inert = i !== idx; });
    }
  }

  /* ======================================================================
     <ak-accordion> — accessible disclosure list (works in any section)
     ====================================================================== */
  class AkAccordion extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ak-acc-btn]');
        if (!btn || !this.contains(btn)) return;
        var region = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (region) region.toggleAttribute('data-open', open);
      }.bind(this), { signal: this.ac.signal });
    }
    disconnectedCallback() { this.ac && this.ac.abort(); }
  }

  /* ======================================================================
     <ak-scroller> — horizontal row with prev/next buttons (shades, related)
     ====================================================================== */
  class AkScroller extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.bind();
    }
    bind() {
      this.row = this.querySelector('[data-ak-row]');
      this.prev = this.querySelector('[data-ak-row-prev]');
      this.next = this.querySelector('[data-ak-row-next]');
      if (!this.row) return;
      var sig = { signal: this.ac.signal };
      var self = this;
      if (this.prev) this.prev.addEventListener('click', function () { self.by(-1); }, sig);
      if (this.next) this.next.addEventListener('click', function () { self.by(1); }, sig);
      this.row.addEventListener('scroll', this.update.bind(this), { signal: this.ac.signal, passive: true });
      window.addEventListener('resize', this.update.bind(this), { signal: this.ac.signal, passive: true });
      this.update();
    }
    rebind() { this.ac.abort(); this.ac = new AbortController(); this.bind(); }
    by(dir) { this.row.scrollBy({ left: dir * this.row.clientWidth * 0.8, behavior: reduceMotion() ? 'auto' : 'smooth' }); }
    update() {
      var r = this.row; if (!r) return;
      var max = r.scrollWidth - r.clientWidth - 2;
      if (this.prev) this.prev.disabled = r.scrollLeft <= 2;
      if (this.next) this.next.disabled = r.scrollLeft >= max;
      var controls = this.querySelector('[data-ak-row-controls]');
      if (controls) controls.hidden = max <= 0;
    }
    disconnectedCallback() { this.ac && this.ac.abort(); }
  }

  /* ======================================================================
     <ak-shades> — option-value cards that drive the main variant picker
     ====================================================================== */
  class AkShades extends AkScroller {
    connectedCallback() {
      super.connectedCallback();
      var sig = { signal: this.ac.signal };
      var self = this;
      this.addEventListener('click', function (e) {
        var card = e.target.closest('[data-ak-shade]');
        if (!card) return;
        document.dispatchEvent(new CustomEvent('ak:select-option', { detail: { index: +self.dataset.optionIndex, value: card.dataset.value, scroll: true } }));
      }, sig);
      document.addEventListener('ak:variant-change', function (e) {
        var opts = (e.detail && e.detail.options) || [];
        self.mark(opts[+self.dataset.optionIndex]);
      }, sig);
    }
    mark(value) {
      var selectedText = this.dataset.textSelected || '';
      this.querySelectorAll('[data-ak-shade]').forEach(function (c) {
        var on = c.dataset.value === value;
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
        var st = c.querySelector('[data-ak-shade-state]');
        if (st && !c.hasAttribute('data-soldout')) st.textContent = on ? selectedText : '';
      });
    }
  }

  /* ======================================================================
     <ak-recommendations> — Shopify product recommendations via Section Rendering
     ====================================================================== */
  class AkRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) { this.finish(); return; }
      var self = this;
      this.ctrl = new AbortController();
      fetch(this.dataset.url, { signal: this.ctrl.signal })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.querySelector('ak-recommendations');
          if (fresh) self.innerHTML = fresh.innerHTML;
          self.dataset.loaded = '1';
          if (fresh && fresh.hasAttribute('data-empty')) self.setAttribute('data-empty', '');
          self.finish();
        })
        .catch(function (e) { if (e.name !== 'AbortError') { console.warn('[ak-pdp] recommendations', e); self.setAttribute('data-empty', ''); self.finish(); } });
    }
    finish() {
      var section = this.closest('[data-ak-related]');
      var empty = this.hasAttribute('data-empty') || !this.querySelector('.ak-pcard');
      if (section) section.hidden = empty && !(window.Shopify && window.Shopify.designMode);
      var scroller = this.closest('ak-scroller');
      if (scroller && scroller.rebind) scroller.rebind();
      initReveal(this);
    }
    disconnectedCallback() { this.ctrl && this.ctrl.abort(); }
  }

  /* ======================================================================
     <ak-newsletter> — client validation + loading state for Shopify customer form
     ====================================================================== */
  class AkNewsletter extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      var form = this.querySelector('form');
      if (!form) return;
      var input = form.querySelector('input[type="email"]');
      var err = this.querySelector('[data-ak-nl-error]');
      var btn = form.querySelector('button[type="submit"]');
      var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', function (e) {
        var ok = input && re.test(input.value.trim());
        if (!ok) {
          e.preventDefault();
          if (err) { err.textContent = err.dataset.message || ''; err.hidden = false; }
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        if (btn) { btn.setAttribute('aria-busy', 'true'); }
      }, { signal: this.ac.signal });
      if (input) input.addEventListener('input', function () {
        input.removeAttribute('aria-invalid');
        if (err) err.hidden = true;
      }, { signal: this.ac.signal });
    }
    disconnectedCallback() { this.ac && this.ac.abort(); }
  }

  var defs = { 'ak-product': AkProduct, 'ak-accordion': AkAccordion, 'ak-scroller': AkScroller, 'ak-shades': AkShades, 'ak-recommendations': AkRecommendations, 'ak-newsletter': AkNewsletter };
  Object.keys(defs).forEach(function (n) { if (!customElements.get(n)) customElements.define(n, defs[n]); });

  function boot() { initReveal(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); });

  window.AKPDP = { MoneyFormatter: MoneyFormatter, initReveal: initReveal };
})();
